// Cache configuration
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

class CacheItem {
  constructor(value) {
    this.value = value;
    this.timestamp = Date.now();
  }

  isExpired() {
    return Date.now() - this.timestamp > CACHE_TTL;
  }
}

class Cache {
  constructor() {
    this.cache = new Map();
    this.subscribers = new Map();
    this.version = 0;
  }

  subscribe(pattern, callback) {
    if (!this.subscribers.has(pattern)) {
      this.subscribers.set(pattern, new Set());
    }
    this.subscribers.get(pattern).add(callback);
    return () => {
      const callbacks = this.subscribers.get(pattern);
      if (callbacks) {
        callbacks.delete(callback);
      }
    };
  }

  notifySubscribers(key) {
    this.version++; // Increment version to force re-renders
    this.subscribers.forEach((callbacks, pattern) => {
      if (key.includes(pattern)) {
        callbacks.forEach(callback => callback(this.version));
      }
    });
  }

  set(key, value) {
    this.cache.set(key, new CacheItem(value));
    this.notifySubscribers(key);
  }

  get(key) {
    const item = this.cache.get(key);
    if (!item) return null;
    if (item.isExpired()) {
      this.cache.delete(key);
      return null;
    }
    return item.value;
  }

  clear() {
    this.cache.clear();
    this.notifySubscribers('*');
  }

  delete(key) {
    this.cache.delete(key);
    this.notifySubscribers(key);
  }

  // Alias for delete - used for cache invalidation
  invalidate(key) {
    this.delete(key);
  }

  // Clear expired items
  cleanup() {
    let hasDeleted = false;
    for (const [key, item] of this.cache.entries()) {
      if (item.isExpired()) {
        this.cache.delete(key);
        hasDeleted = true;
      }
    }
    if (hasDeleted) {
      this.notifySubscribers('*');
    }
  }
}

// Create a single cache instance to be used across the app
export const apiCache = new Cache();

// Run cleanup every minute
setInterval(() => {
  apiCache.cleanup();
}, 60 * 1000);